import {
    AuctionCatalogsService,
    BiddingService,
    GetLotsByAuctionId200Response,
    GetMyBidHistory200Response,
    UserBidHistoryResponse,
} from '@viewbid/ts-angular-client';
import { FilterValue, fmParseNum } from '@viewbid/ui';
import {
    catchError,
    concat,
    defer,
    EMPTY,
    finalize,
    from,
    map,
    Observable,
    of,
    Subscription,
    switchMap,
    tap,
} from 'rxjs';
import { TimerService } from '@/shared/services/timerService';
import { SocketService } from 'src/app/viewbid/shared/services/socket.service';
import {
    LotResponseWithProxy,
    normalizeLotRow,
} from 'src/app/viewbid/shared/utils/lot-card-mapper.util';

export interface LotPageQuery {
    auctionId: string;
    page: number;
    pageSize: number;
    sortBy: string;
    searchQuery?: string;
    filter: FilterValue;
    showClosedLots: boolean;
    windowSize: number;
    userId: string;
}

interface MergedLots {
    rows: LotResponseWithProxy[];
    userBidLotIds: string[];
}

export interface LotPageCacheEntry extends MergedLots {
    totalItems: number;
    page: number;
    pageSize: number;
    queryKey: string;
    generation: number;
}

interface LotPageRequest {
    generation: number;
    subscription: Subscription;
    lotIds: Set<string>;
    joinsSettled: boolean;
    resyncAfter: Promise<void> | null;
    patches: Map<string, Partial<LotResponseWithProxy>>;
}

/** Owns retained pages and their REST, socket-room and timer lifetimes. */
export class AuctionLotPageCache {
    private query: LotPageQuery | null = null;
    private readonly lotPageCache: Map<number, LotPageCacheEntry> = new Map();
    private lotPageCacheKey: string = '';
    private committedPage: LotPageCacheEntry | null = null;
    private cacheGeneration: number = 0;
    private readonly pageRequests: Map<number, LotPageRequest> = new Map();
    private readonly ownedResourceIds: Set<string> = new Set();
    private readonly pendingResourceSubscriptions: Map<string, Promise<void>> =
        new Map();
    private pendingSocketChanges: Promise<void> | null = null;
    private auctionResourceId: string | null = null;
    private destroyed: boolean = false;

    constructor(
        private auctionService: AuctionCatalogsService,
        private biddingService: BiddingService,
        private socketService: SocketService,
        private timerService: TimerService,
        private onCommit: (entry: LotPageCacheEntry, refresh: boolean) => void,
        private onError: () => void,
    ) {}

    get hasQuery(): boolean {
        return this.query !== null;
    }

    get currentPage(): LotPageCacheEntry | null {
        return this.committedPage;
    }

    load(query: LotPageQuery, onMiss: () => void): void {
        if (this.destroyed) return;
        const previousPage: number | undefined = this.query?.page;
        this.query = {
            ...query,
            filter: { ...query.filter, cats: [...query.filter.cats] },
        };
        const cacheKey: string = this.buildCacheKey(this.query);
        if (cacheKey !== this.lotPageCacheKey) {
            this.clear();
            this.lotPageCacheKey = cacheKey;
        }
        this.prune();
        const page: number = query.showClosedLots ? query.page : 1;
        const cached: LotPageCacheEntry | undefined = query.showClosedLots
            ? this.lotPageCache.get(page)
            : undefined;
        if (cached) {
            this.commit(cached);
            // A prefetched page already has live room ownership. Navigation
            // reuses it; an explicit reload of the current page revalidates.
            if (page !== previousPage) return;
        } else onMiss();
        this.fetchPage(page, true, !!cached);
    }

    /** Keep the displayed page owned until its replacement commits. */
    clear(): void {
        // Fingerprints may repeat (reconnect, A -> B -> A); generations cannot.
        this.cacheGeneration++;
        for (const request of this.pageRequests.values()) {
            request.subscription.unsubscribe();
        }
        this.pageRequests.clear();
        this.lotPageCache.clear();
        this.reconcileSocketSubscriptions();
    }

    destroy(): void {
        if (this.destroyed) return;
        this.destroyed = true;
        this.committedPage = null;
        this.auctionResourceId = null;
        this.clear();
        this.query = null;
    }

    setAuctionResource(id: string | null): void {
        if (this.destroyed) return;
        this.auctionResourceId = id;
        this.reconcileSocketSubscriptions();
    }

    syncCommitted(rows: LotResponseWithProxy[], userBidLotIds: string[]): void {
        if (this.destroyed || !this.committedPage) return;
        this.committedPage.rows = rows;
        this.committedPage.userBidLotIds = [...userBidLotIds];
        this.reconcileSocketSubscriptions();
    }

    /** Replay live changes onto outstanding snapshots, including bid history. */
    patch(
        auctionLotsId: string | undefined,
        patch: Partial<LotResponseWithProxy>,
    ): number {
        if (this.destroyed || !auctionLotsId) return -1;
        // A past end time emits timerComplete synchronously. Let its view
        // callback finish before applying the new snapshot fields.
        if (
            patch.toDatetime &&
            this.ownedResourceIds.has(auctionLotsId) &&
            !this.timerService.hasProcessedEndTime(
                auctionLotsId,
                patch.toDatetime,
            )
        ) {
            this.timerService.resetTimer(auctionLotsId, patch.toDatetime);
        }
        for (const request of this.pageRequests.values()) {
            request.patches.set(auctionLotsId, {
                ...request.patches.get(auctionLotsId),
                ...patch,
            });
        }
        for (const entry of this.retainedPages())
            this.patchEntry(entry, auctionLotsId, patch);
        const idx: number =
            this.committedPage?.rows.findIndex(
                (row: LotResponseWithProxy) =>
                    row.auctionLotsId === auctionLotsId,
            ) ?? -1;
        return idx;
    }

    findLot(auctionLotsId: string): LotResponseWithProxy | undefined {
        for (const entry of this.retainedPages()) {
            const row: LotResponseWithProxy | undefined = entry.rows.find(
                (lot: LotResponseWithProxy) =>
                    lot.auctionLotsId === auctionLotsId,
            );
            if (row) return row;
        }
        return undefined;
    }

    knownBidCount(auctionLotsId: string | undefined): number {
        if (!auctionLotsId) return 0;
        const counts: number[] = [];
        for (const entry of this.retainedPages()) {
            for (const row of entry.rows) {
                if (row.auctionLotsId === auctionLotsId)
                    counts.push(Number(row.bidCount));
            }
        }
        for (const request of this.pageRequests.values()) {
            counts.push(Number(request.patches.get(auctionLotsId)?.bidCount));
        }
        return Math.max(
            0,
            ...counts.filter((count: number) => Number.isFinite(count)),
        );
    }

    /** Page number is excluded so neighbours share the same fingerprint. */
    private buildCacheKey(query: LotPageQuery): string {
        return JSON.stringify({
            auctionId: query.auctionId,
            sortBy: query.sortBy,
            searchQuery: query.searchQuery ?? '',
            cats: [...query.filter.cats].sort(),
            min: query.filter.min,
            max: query.filter.max,
            pageSize: query.pageSize,
            showClosedLots: query.showClosedLots,
            userId: query.userId,
            windowSize: query.showClosedLots
                ? query.pageSize
                : query.windowSize,
        });
    }

    private retainedPages(): Set<LotPageCacheEntry> {
        const entries: Set<LotPageCacheEntry> = new Set();
        if (this.committedPage) entries.add(this.committedPage);
        this.lotPageCache.forEach((entry: LotPageCacheEntry) =>
            entries.add(entry),
        );
        return entries;
    }

    /** A room is released only when no retained page or request needs it. */
    private reconcileSocketSubscriptions(): void {
        const next: Set<string> = new Set();
        if (!this.destroyed) {
            if (this.auctionResourceId) next.add(this.auctionResourceId);
            for (const entry of this.retainedPages()) {
                for (const row of entry.rows) {
                    if (row.auctionLotsId) next.add(row.auctionLotsId);
                }
            }
            for (const request of this.pageRequests.values()) {
                request.lotIds.forEach((id: string) => next.add(id));
            }
        }
        const changes: { resourceId: string; listen: boolean }[] = [];
        for (const resourceId of this.ownedResourceIds) {
            if (next.has(resourceId)) continue;
            changes.push({ resourceId, listen: false });
            this.pendingResourceSubscriptions.delete(resourceId);
            this.timerService.releaseTimer(resourceId);
        }
        for (const resourceId of next) {
            if (!this.ownedResourceIds.has(resourceId)) {
                changes.push({ resourceId, listen: true });
            }
        }
        this.ownedResourceIds.clear();
        next.forEach((id: string) => this.ownedResourceIds.add(id));
        if (!changes.length) return;
        // Keep joins and leaves in order even while the socket connects.
        const subscribe: () => Promise<void> = () =>
            this.socketService.subscribeToResources(changes);
        const subscription: Promise<void> = this.pendingSocketChanges
            ? this.pendingSocketChanges.then(subscribe, subscribe)
            : subscribe();
        this.pendingSocketChanges = subscription;
        for (const change of changes) {
            if (change.listen) {
                this.pendingResourceSubscriptions.set(
                    change.resourceId,
                    subscription,
                );
            }
        }
        const settled: () => void = () => {
            if (this.pendingSocketChanges === subscription) {
                this.pendingSocketChanges = null;
            }
            for (const change of changes) {
                if (
                    this.pendingResourceSubscriptions.get(change.resourceId) ===
                    subscription
                ) {
                    this.pendingResourceSubscriptions.delete(change.resourceId);
                }
            }
        };
        subscription.then(settled, settled);
    }

    private isPageInWindow(page: number): boolean {
        return !!this.query && Math.abs(page - this.query.page) <= 1;
    }

    private prune(): void {
        for (const page of this.lotPageCache.keys()) {
            if (!this.isPageInWindow(page)) this.lotPageCache.delete(page);
        }
        for (const [page, request] of this.pageRequests) {
            if (!this.isPageInWindow(page)) request.subscription.unsubscribe();
        }
        this.reconcileSocketSubscriptions();
    }

    private prefetchNeighbours(): void {
        const entry: LotPageCacheEntry | null = this.committedPage;
        if (
            this.destroyed ||
            !this.query?.showClosedLots ||
            !entry ||
            entry.generation !== this.cacheGeneration ||
            entry.queryKey !== this.lotPageCacheKey ||
            entry.page !== this.query.page
        )
            return;
        const totalPages: number = Math.ceil(entry.totalItems / entry.pageSize);
        for (const page of [entry.page - 1, entry.page + 1]) {
            if (page < 1 || page > totalPages) continue;
            if (this.lotPageCache.has(page) || this.pageRequests.has(page))
                continue;
            this.fetchPage(page, false, false);
        }
    }

    private mergeProxyBidsInto(
        rows: LotResponseWithProxy[],
        userId: string,
    ): Observable<MergedLots> {
        if (!rows.length || !userId) {
            return of({ rows, userBidLotIds: [] });
        }
        const lotIds: string[] = rows.map(
            (row: LotResponseWithProxy) => row.auctionLotsId!,
        );
        return this.biddingService
            .getMyBidHistory(1, Math.max(100, lotIds.length), lotIds.toString())
            .pipe(
                map((bidRes: GetMyBidHistory200Response) => {
                    const merged: LotResponseWithProxy[] = rows.map(
                        (row: LotResponseWithProxy) => {
                            const match: UserBidHistoryResponse | undefined =
                                bidRes.data?.find(
                                    (b: UserBidHistoryResponse) =>
                                        b.auctionLotId === row.auctionLotsId,
                                );
                            return match
                                ? { ...row, maximumProxyBid: match.userBid }
                                : row;
                        },
                    );
                    const userBidLotIds: string[] =
                        bidRes.data
                            ?.map((b: UserBidHistoryResponse) => b.auctionLotId)
                            .filter(
                                (id: string | undefined): id is string => !!id,
                            ) ?? [];
                    return { rows: merged, userBidLotIds };
                }),
                catchError(() =>
                    of({
                        rows: rows.map((row: LotResponseWithProxy) => ({
                            ...row,
                            maximumProxyBid: 0,
                        })),
                        userBidLotIds: [] as string[],
                    }),
                ),
            );
    }

    /** Discover rooms without making the REST snapshot wait for connectivity. */
    private fetchSubscribedLots(
        query: LotPageQuery,
        page: number,
        pageSize: number,
        request: LotPageRequest,
        refresh: boolean,
    ): Observable<GetLotsByAuctionId200Response> {
        // Earlier patches precede this read; replay updates received during
        // this snapshot/history chain over its response.
        request.patches.clear();
        return this.auctionService
            .getLotsByAuctionId(
                query.auctionId,
                page,
                pageSize,
                '',
                undefined,
                query.searchQuery
                    ? `${query.sortBy},${query.searchQuery}`
                    : query.sortBy,
                query.filter.cats.length
                    ? query.filter.cats.join(',')
                    : undefined,
                fmParseNum(query.filter.min) ?? undefined,
                fmParseNum(query.filter.max) ?? undefined,
                query.showClosedLots,
            )
            .pipe(
                tap((response: GetLotsByAuctionId200Response) => {
                    const lotIds: string[] = response.data
                        .map(normalizeLotRow)
                        .map((row: LotResponseWithProxy) => row.auctionLotsId)
                        .filter((id: string | undefined): id is string => !!id);
                    lotIds.forEach((id: string) => request.lotIds.add(id));
                    this.reconcileSocketSubscriptions();
                    if (!refresh) {
                        const joins: Promise<void>[] = [
                            ...new Set(
                                lotIds
                                    .map((id: string) =>
                                        this.pendingResourceSubscriptions.get(
                                            id,
                                        ),
                                    )
                                    .filter(
                                        (
                                            join: Promise<void> | undefined,
                                        ): join is Promise<void> => !!join,
                                    ),
                            ),
                        ];
                        let unsettled: number = joins.length;
                        request.joinsSettled = unsettled === 0;
                        const settled: () => void = (): void => {
                            request.joinsSettled = --unsettled === 0;
                        };
                        request.resyncAfter = joins.length
                            ? Promise.all(
                                  joins.map((join: Promise<void>) =>
                                      join.then(settled, settled),
                                  ),
                              ).then((): void => undefined)
                            : null;
                    }
                }),
            );
    }

    /** A page request owns both snapshots and their complete history chains. */
    private fetchPage(
        page: number,
        foreground: boolean,
        silent: boolean,
    ): void {
        if (this.destroyed || !this.query) return;
        this.pageRequests.get(page)?.subscription.unsubscribe();
        const request: LotPageRequest = {
            generation: this.cacheGeneration,
            subscription: new Subscription(),
            lotIds: new Set(),
            joinsSettled: true,
            resyncAfter: null,
            patches: new Map(),
        };
        const query: LotPageQuery = this.query;
        const queryKey: string = this.lotPageCacheKey;
        const pageSize: number = query.showClosedLots
            ? query.pageSize
            : query.windowSize;
        this.pageRequests.set(page, request);
        let refreshing: boolean = false;
        const snapshot: () => Observable<LotPageCacheEntry> = () =>
            this.fetchSubscribedLots(
                query,
                page,
                pageSize,
                request,
                refreshing,
            ).pipe(
                switchMap((response: GetLotsByAuctionId200Response) =>
                    this.mergeProxyBidsInto(
                        response.data.map(normalizeLotRow),
                        query.userId,
                    ).pipe(
                        map(
                            (merged: MergedLots): LotPageCacheEntry => ({
                                ...merged,
                                totalItems: Number(response.totalCount),
                                page: Number(response.page) || page,
                                pageSize: Number(response.pageSize) || pageSize,
                                queryKey,
                                generation: request.generation,
                            }),
                        ),
                    ),
                ),
            );
        request.subscription.add(
            concat(
                defer(snapshot),
                defer(() =>
                    request.resyncAfter === null
                        ? EMPTY
                        : from(request.resyncAfter).pipe(
                              switchMap(() => {
                                  if (!this.isCurrentRequest(page, request))
                                      return EMPTY;
                                  refreshing = true;
                                  return snapshot();
                              }),
                              catchError(() => EMPTY),
                          ),
                ),
            )
                .pipe(
                    finalize(() => {
                        if (this.pageRequests.get(page) === request) {
                            this.pageRequests.delete(page);
                        }
                        this.reconcileSocketSubscriptions();
                    }),
                )
                .subscribe({
                    next: (entry: LotPageCacheEntry) => {
                        if (!this.isCurrentRequest(page, request)) return;
                        // Decide at the first commit, without waiting for the
                        // socket. Only joins still pending now need a resync.
                        if (!refreshing && request.joinsSettled) {
                            request.resyncAfter = null;
                        }
                        for (const [lotId, patch] of request.patches) {
                            this.patchEntry(entry, lotId, patch);
                        }
                        if (query.showClosedLots && this.isPageInWindow(page)) {
                            this.lotPageCache.set(page, entry);
                        }
                        if (page === this.requestedPage()) {
                            this.commit(entry, refreshing);
                        } else {
                            this.reconcileSocketSubscriptions();
                        }
                    },
                    error: () => {
                        if (
                            !this.isCurrentRequest(page, request) ||
                            !foreground ||
                            page !== this.requestedPage() ||
                            silent
                        )
                            return;
                        this.onError();
                    },
                }),
        );
    }

    private requestedPage(): number {
        return this.query?.showClosedLots ? this.query.page : 1;
    }

    private isCurrentRequest(page: number, request: LotPageRequest): boolean {
        return (
            !this.destroyed &&
            request.generation === this.cacheGeneration &&
            this.pageRequests.get(page) === request
        );
    }

    private commit(entry: LotPageCacheEntry, refresh: boolean = false): void {
        this.committedPage = entry;
        this.reconcileSocketSubscriptions();
        this.onCommit(entry, refresh);
        this.prefetchNeighbours();
    }

    private patchEntry(
        entry: MergedLots,
        auctionLotsId: string,
        patch: Partial<LotResponseWithProxy>,
    ): void {
        entry.rows = entry.rows.map((row: LotResponseWithProxy) =>
            row.auctionLotsId === auctionLotsId
                ? {
                      ...row,
                      ...patch,
                      bidCount: Math.max(
                          row.bidCount ?? 0,
                          patch.bidCount ?? 0,
                      ),
                  }
                : row,
        );
        if (patch.maximumProxyBid !== undefined) {
            entry.userBidLotIds = entry.userBidLotIds.filter(
                (id: string) => id !== auctionLotsId,
            );
            if (
                patch.maximumProxyBid > 0 &&
                entry.rows.some(
                    (row: LotResponseWithProxy) =>
                        row.auctionLotsId === auctionLotsId,
                )
            )
                entry.userBidLotIds.push(auctionLotsId);
        }
    }
}
